import type { ChatMessage, UserFrontendChatMessage } from "../messages/types";
import type { IndexedAgentEvent, LoadedChat } from "../types";
import { aggregateToolProgressTimelineItems } from "../utils/statusMessages";
import { ChatStreamError, type FileRef } from "../utils/streamChatMessage";
import {
  ANALYSIS_CHAT_TURN_ENDED_STATUS,
  type AnalysisChatEvent,
  type ToolProgressPayload,
} from "@/backend/analysis-chat-v2/types";
import type { AgentRunId } from "@/components/analysis/agentic/db_types";
import { sleep } from "@/lib/utils";
import { logger } from "@/utils/logging/browser";
import { reasonTag } from "@/utils/o11y/tags";
import type { FrontendChatId, FrontendChatMessageId } from "@/utils/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

type AssistantMessage<T extends FrontendChatMessageId> = Extract<
  ChatMessage<T>,
  { role: "assistant" }
>;
type AssistantIteration<T extends FrontendChatMessageId> =
  AssistantMessage<T>["iterations"][number];

const STREAM_STALL_MS = 15_000;
// 10 total attempts (1 initial + 9 retries) and up to ~198s worst-case
// wall time if every attempt hits the 15s stall timeout.
const RETRY_BACKOFF_MS = [
  0, 0, 1_000, 2_000, 5_000, 10_000, 10_000, 10_000, 10_000,
];

type StreamAbortReason = "manual" | "stall" | null;
export type StreamMode = "send" | "retry" | "resume";
type RunStreamResult =
  | "completed"
  | "recovered"
  | "failed"
  | "aborted"
  | "stale";

// Every send/retry/resume attempt owns one operation record. Async callbacks
// must prove they still belong to the active operation before mutating UI state.
type StreamOperation<
  T extends FrontendChatId,
  U extends FrontendChatMessageId,
> = {
  opId: number;
  mode: StreamMode;
  chatId: T;
  assistantMessageId: U;
  userMessageId: U;
  lastEventIndex: number;
  lastActivityAt: number;
  langsmithTraceId?: string;
};

// Chat-scoped UI error state. When a send/retry/resume flow exhausts recovery,
// we keep the failure attached to that chat so another selected chat does not
// inherit the blocked composer/error message.
type BlockedChatState<T extends FrontendChatId> = {
  chatId: T;
  message: string;
  source: StreamMode;
} | null;

type SendingOperationState<T extends FrontendChatId> = {
  chatId: T;
  opId: number;
} | null;

type UpsertAssistantMessageInChat<
  T extends FrontendChatId,
  U extends FrontendChatMessageId,
> = (
  chatId: T,
  messageId: U,
  buildMessage: (existing: ChatMessage<U> | null) => ChatMessage<U>
) => void;

export type ChatStreamEventContext<
  T extends FrontendChatId,
  U extends FrontendChatMessageId,
> = {
  chatId: T;
  assistantMessageId: U;
  userMessageId: U;
  mode: StreamMode;
  eventIndex: number;
  event: AnalysisChatEvent;
};

export type ChatStreamOperationEvent<
  T extends FrontendChatId,
  U extends FrontendChatMessageId,
> =
  | {
      type: "retry";
      chatId: T;
      userMessageId: U;
      assistantMessageId: U;
      mode: StreamMode;
      attempt: number;
      reason: "stall" | "error";
    }
  | {
      type: "retry-exhausted";
      chatId: T;
      userMessageId: U;
      assistantMessageId: U;
      mode: StreamMode;
    }
  | {
      type: "error";
      chatId: T;
      userMessageId: U;
      assistantMessageId: U;
      mode: StreamMode;
      error: string;
      source: "stream" | "retry";
    }
  | {
      type: "aborted";
      chatId: T;
      userMessageId: U;
      assistantMessageId: U;
      mode: StreamMode;
    };

export type SendChatMessageArgs<
  T extends FrontendChatId,
  U extends FrontendChatMessageId,
  TSendOptions,
> = {
  chatId: T;
  messageId: U;
  message: string;
  replayFrom?: number;
  replayOnly?: boolean;
  isNewChat?: boolean;
  files?: FileRef[];
  sendOptions?: TSendOptions;
  signal: AbortSignal;
  onEvent: (event: IndexedAgentEvent) => void;
  onStarted?: () => void;
  onStreamActivity?: () => void;
};

/**
 * Transport callback that drives one chat stream attempt.
 *
 * Contract (callers MUST satisfy these):
 * - MUST throw `ChatStreamError` for any non-2xx HTTP response, including 5xx.
 *   Every `ChatStreamError` is treated as definitive and non-retryable.
 * - MUST let other thrown errors (network drops, parse failures, generic
 *   `Error`) bubble; the hook treats those as retryable.
 * - MUST honor the provided `signal: AbortSignal`. The hook aborts the signal
 *   on manual abort, stall detection, and chat switches; `onSend` must not
 *   swallow `AbortError`.
 * - SHOULD call `onStarted()` once the response is open and ready to stream.
 * - SHOULD call `onStreamActivity()` whenever bytes arrive so the stall timer
 *   resets.
 */
export type OnSend<
  T extends FrontendChatId,
  U extends FrontendChatMessageId,
  TSendOptions,
> = (args: SendChatMessageArgs<T, U, TSendOptions>) => Promise<void>;

const safelyInvoke = (label: string, fn: () => void): void => {
  try {
    fn();
  } catch (error) {
    console.error(`[useChatStreaming] ${label} threw`, error);
    logger.captureException(
      error instanceof Error ? error : new Error(String(error)),
      { handler: `useChatStreaming/${label}` }
    );
  }
};

const createEmptyIteration = <
  T extends FrontendChatMessageId,
>(): AssistantIteration<T> => ({
  reportItems: [],
  statusMessages: [],
  timestamp: new Date(),
});

const cloneIterations = <T extends FrontendChatMessageId>(
  iterations: AssistantIteration<T>[]
): AssistantIteration<T>[] =>
  iterations.map((iteration) => ({
    ...iteration,
    reportItems: [...(iteration.reportItems ?? [])],
    statusMessages: [...(iteration.statusMessages ?? [])],
  }));

const ensureIterations = <T extends FrontendChatMessageId>(
  iterations: AssistantIteration<T>[]
): AssistantIteration<T>[] => {
  if (iterations.length === 0) {
    return [createEmptyIteration()];
  }
  return cloneIterations(iterations);
};

const updateLastIteration = <T extends FrontendChatMessageId>({
  iterations,
  updater,
}: {
  iterations: AssistantIteration<T>[];
  updater: (iteration: AssistantIteration<T>) => AssistantIteration<T>;
}): AssistantIteration<T>[] => {
  const lastIndex = iterations.length - 1;
  return iterations.map((iteration, index) =>
    index === lastIndex ? updater(iteration) : iteration
  );
};

const getAssistantMessage = <T extends FrontendChatMessageId>({
  existing,
  assistantMessageId,
  fallbackTimestamp,
}: {
  existing: ChatMessage<T> | null;
  assistantMessageId: T;
  fallbackTimestamp: Date;
}): AssistantMessage<T> =>
  existing && existing.role === "assistant"
    ? existing
    : {
        id: assistantMessageId,
        role: "assistant",
        iterations: [],
        timestamp: fallbackTimestamp,
      };

/**
 * Routes a status event to the iteration that already has a status with this id
 * (for late-arriving events like summarizer after done()), or to the last iteration if new.
 */
const upsertIterationWithStatusId = <
  T extends FrontendChatId,
  U extends FrontendChatMessageId,
>({
  chatId,
  upsertAssistantMessageInChat,
  assistantMessageId,
  status,
}: {
  chatId: T;
  upsertAssistantMessageInChat: UpsertAssistantMessageInChat<T, U>;
  assistantMessageId: U;
  status: ToolProgressPayload;
}) => {
  const timestamp = new Date();
  upsertAssistantMessageInChat(chatId, assistantMessageId, (existing) => {
    const baseMessage = getAssistantMessage({
      existing,
      assistantMessageId,
      fallbackTimestamp: timestamp,
    });
    const iterations = ensureIterations(baseMessage.iterations);

    const ownerIndex = iterations.findIndex((iter) =>
      (iter.statusMessages ?? []).some((s) => s.id === status.id)
    );
    const targetIndex = ownerIndex >= 0 ? ownerIndex : iterations.length - 1;

    return {
      ...baseMessage,
      iterations: iterations.map((iter, i) =>
        i === targetIndex
          ? {
              ...iter,
              statusMessages: aggregateToolProgressTimelineItems(
                iter.statusMessages ?? [],
                status
              ),
            }
          : iter
      ),
    };
  });
};

/**
 * Helper to update the last iteration of an assistant message.
 * Handles all the boilerplate: get/create message, ensure iterations, update last iteration.
 */
const upsertLastIteration = <
  T extends FrontendChatId,
  U extends FrontendChatMessageId,
>({
  chatId,
  upsertAssistantMessageInChat,
  assistantMessageId,
  updater,
}: {
  chatId: T;
  upsertAssistantMessageInChat: UpsertAssistantMessageInChat<T, U>;
  assistantMessageId: U;
  updater: (iteration: AssistantIteration<U>) => AssistantIteration<U>;
}) => {
  const timestamp = new Date();
  upsertAssistantMessageInChat(chatId, assistantMessageId, (existing) => {
    const baseMessage = getAssistantMessage({
      existing,
      assistantMessageId,
      fallbackTimestamp: timestamp,
    });

    return {
      ...baseMessage,
      iterations: updateLastIteration({
        iterations: ensureIterations(baseMessage.iterations),
        updater,
      }),
    };
  });
};

class RetryableStreamError extends Error {
  constructor({ message }: { message: string }) {
    super(message);
    this.name = "RetryableStreamError";
  }
}

const GENERIC_STREAM_ERROR_MESSAGE =
  "Connection interrupted while generating the response. Please try again.";

// Iterations are saved mid-turn, so an assistant message only counts once the server no longer marks the turn pending.
export const hasSuccessfulAssistantAfterUser = <
  T extends FrontendChatId,
  U extends FrontendChatMessageId,
>({
  chat,
  userMessageId,
}: {
  chat: LoadedChat<T, U>;
  userMessageId: U;
}): boolean => {
  const userIndex = chat.messages.findIndex(
    (message) => message.id === userMessageId
  );
  if (userIndex < 0 || chat.pendingMessageId === userMessageId) {
    return false;
  }
  return chat.messages
    .slice(userIndex + 1)
    .some(
      (message) =>
        message.role === "assistant" &&
        !message.iterations[message.iterations.length - 1]?.error
    );
};

export const getUserFacingStreamError = ({
  error,
}: {
  error: unknown;
}): string => {
  if (error instanceof ChatStreamError) {
    if (error.status === 401 || error.status === 403) {
      return "You no longer have access. Reload or sign in.";
    }
    if (error.status === 404) {
      return "Chat not found. It may have been deleted.";
    }
    if (error.status === 429) {
      return "Too many requests. Please try again later.";
    }
    if (error.status >= 500) {
      return error.message;
    }
    return error.message;
  }
  return GENERIC_STREAM_ERROR_MESSAGE;
};

const handleStreamEvent = <
  T extends FrontendChatId,
  U extends FrontendChatMessageId,
>(
  envelope: IndexedAgentEvent,
  {
    operation,
    activeOperationRef,
    terminalEventRef,
    isCurrentOperation,
    upsertAssistantMessageInChat,
    updateChatMetadataForChat,
    setAgentRunIdForChat,
    currentChatRef,
    onStreamEvent,
    onOperationEvent,
  }: {
    operation: StreamOperation<T, U>;
    activeOperationRef: React.RefObject<StreamOperation<T, U> | null>;
    terminalEventRef: { current: boolean };
    isCurrentOperation: (operation: StreamOperation<T, U>) => boolean;
    upsertAssistantMessageInChat: UpsertAssistantMessageInChat<T, U>;
    updateChatMetadataForChat: (
      chatId: T,
      updates: {
        title?: string;
        updatedAt?: Date;
        isPersisted?: boolean;
      }
    ) => void;
    setAgentRunIdForChat?: (chatId: T, agentRunId: AgentRunId) => void;
    currentChatRef: React.RefObject<LoadedChat<T, U> | null>;
    onStreamEvent?: (context: ChatStreamEventContext<T, U>) => void;
    onOperationEvent?: (event: ChatStreamOperationEvent<T, U>) => void;
  }
) => {
  if (!isCurrentOperation(operation)) {
    return;
  }

  const state = activeOperationRef.current;
  if (!state) {
    console.warn("[Event] No streaming state, ignoring event");
    return;
  }

  const updatedState: StreamOperation<T, U> = {
    ...state,
    lastActivityAt: Date.now(),
    lastEventIndex: Math.max(state.lastEventIndex, envelope.eventIndex),
  };
  activeOperationRef.current = updatedState;

  const assistantMessageId = updatedState.assistantMessageId;
  const chatId = updatedState.chatId;
  const event = envelope.event;

  switch (event.type) {
    case "init": {
      if (event.agentRunId && setAgentRunIdForChat) {
        setAgentRunIdForChat(chatId, event.agentRunId);
      }

      if (event.langsmithTraceId && isCurrentOperation(operation)) {
        activeOperationRef.current = {
          ...updatedState,
          langsmithTraceId: event.langsmithTraceId,
        };
      }
      break;
    }

    case "iteration-start": {
      upsertAssistantMessageInChat(chatId, assistantMessageId, (existing) => {
        const baseMessage = getAssistantMessage({
          existing,
          assistantMessageId,
          fallbackTimestamp: new Date(),
        });

        return {
          ...baseMessage,
          iterations: [
            ...cloneIterations(baseMessage.iterations),
            createEmptyIteration(),
          ],
        };
      });
      break;
    }

    case "tool-progress": {
      const timestamp = new Date(event.timestamp);
      const { type: _type, ...status } = event;
      upsertIterationWithStatusId({
        chatId,
        upsertAssistantMessageInChat,
        assistantMessageId,
        status: { ...status, timestamp: timestamp.getTime() },
      });
      break;
    }

    case "debug":
    case "navigate":
    case "agent-questions":
      break;

    case "report-item": {
      upsertLastIteration({
        chatId,
        upsertAssistantMessageInChat,
        assistantMessageId,
        updater: (iteration) => ({
          ...iteration,
          reportItems: [...iteration.reportItems, event.item],
        }),
      });
      break;
    }

    case "report-complete": {
      // Replace all streamed items with the final parsed result (includes web citations)
      upsertLastIteration({
        chatId,
        upsertAssistantMessageInChat,
        assistantMessageId,
        updater: (iteration) => ({
          ...iteration,
          reportItems: event.items,
        }),
      });
      break;
    }

    case "error": {
      upsertLastIteration({
        chatId,
        upsertAssistantMessageInChat,
        assistantMessageId,
        updater: (iteration) => ({ ...iteration, error: event.error }),
      });
      safelyInvoke("onOperationEvent", () =>
        onOperationEvent?.({
          type: "error",
          chatId,
          userMessageId: operation.userMessageId,
          assistantMessageId,
          mode: operation.mode,
          error: event.error,
          source: "stream",
        })
      );
      terminalEventRef.current = true;
      break;
    }

    case "finish": {
      const langsmithTraceId = activeOperationRef.current?.langsmithTraceId;
      if (langsmithTraceId) {
        upsertAssistantMessageInChat(chatId, assistantMessageId, (existing) => {
          const baseMessage = getAssistantMessage({
            existing,
            assistantMessageId,
            fallbackTimestamp: new Date(),
          });
          return { ...baseMessage, langsmithTraceId };
        });
      }

      terminalEventRef.current = true;

      if (
        currentChatRef.current?.id === chatId &&
        !currentChatRef.current.isPersisted
      ) {
        updateChatMetadataForChat(chatId, {
          isPersisted: true,
          updatedAt: new Date(),
        });
      }
      break;
    }

    case "invalidate-queries":
    case "invalidate-feature-table":
      // Server-driven cache sync events are surfaced to callers via
      // `onStreamEvent`; the hook does not invalidate React Query directly.
      break;
  }

  safelyInvoke("onStreamEvent", () =>
    onStreamEvent?.({
      chatId,
      assistantMessageId,
      userMessageId: operation.userMessageId,
      mode: operation.mode,
      eventIndex: envelope.eventIndex,
      event,
    })
  );
};

export const useChatStreaming = <
  T extends FrontendChatId,
  U extends FrontendChatMessageId,
  TSendOptions = undefined,
>({
  chatId,
  chat,
  appendMessageToChat,
  upsertAssistantMessageInChat,
  updateChatMetadataForChat,
  fetchChat,
  refreshChat,
  setAgentRunIdForChat,
  onSend,
  onStreamEvent,
  onOperationEvent,
  streamStallMs = STREAM_STALL_MS,
}: {
  chatId: T;
  chat: LoadedChat<T, U> | null;
  appendMessageToChat: (chatId: T, message: ChatMessage<U>) => void;
  upsertAssistantMessageInChat: UpsertAssistantMessageInChat<T, U>;
  updateChatMetadataForChat: (
    chatId: T,
    updates: {
      title?: string;
      updatedAt?: Date;
      isPersisted?: boolean;
    }
  ) => void;
  fetchChat: (chatId: T) => Promise<LoadedChat<T, U> | null>;
  refreshChat: (chatId: T) => Promise<LoadedChat<T, U> | null>;
  setAgentRunIdForChat?: (chatId: T, agentRunId: AgentRunId) => void;
  onSend: OnSend<T, U, TSendOptions>;
  onStreamEvent?: (context: ChatStreamEventContext<T, U>) => void;
  onOperationEvent?: (event: ChatStreamOperationEvent<T, U>) => void;
  streamStallMs?: number;
}) => {
  // Track sending ownership by operation, not just chat, so a stale finally
  // from an older op cannot clear the UI for a newer send on the same chat.
  const [sendingOperation, setSendingOperation] =
    useState<SendingOperationState<T>>(null);
  const [blockedChatState, setBlockedChatState] =
    useState<BlockedChatState<T>>(null);
  const streamAbortRef = useRef<AbortController | null>(null);
  const abortReasonRef = useRef<StreamAbortReason>(null);
  const activeOperationRef = useRef<StreamOperation<T, U> | null>(null);
  const nextOperationIdRef = useRef(0);
  // Async callbacks can outlive the render that created them, so these refs
  // always expose the latest selected/current chat during staleness checks.
  const selectedChatIdRef = useRef<T>(chatId);
  const currentChatRef = useRef<LoadedChat<T, U> | null>(chat);

  useEffect(() => {
    selectedChatIdRef.current = chatId;
  }, [chatId]);

  useEffect(() => {
    currentChatRef.current = chat;
  }, [chat]);

  const clearBlockedStateForChat = useCallback((chatId: T) => {
    setBlockedChatState((prev) => (prev?.chatId === chatId ? null : prev));
  }, []);

  const finishOperationIfCurrent = useCallback(
    (operation: StreamOperation<T, U>) => {
      if (
        activeOperationRef.current?.opId === operation.opId &&
        activeOperationRef.current?.chatId === operation.chatId
      ) {
        activeOperationRef.current = null;
      }
      setSendingOperation((prev) =>
        prev?.opId === operation.opId && prev.chatId === operation.chatId
          ? null
          : prev
      );
    },
    []
  );

  const abort = useCallback(() => {
    abortReasonRef.current = "manual";
    const activeOperation = activeOperationRef.current;
    activeOperationRef.current = null;
    streamAbortRef.current?.abort();
    streamAbortRef.current = null;
    if (activeOperation) {
      setSendingOperation((prev) =>
        prev?.opId === activeOperation.opId &&
        prev.chatId === activeOperation.chatId
          ? null
          : prev
      );
    }
  }, []);

  // The hook can survive a chat switch without remounting, so changing the
  // selected chat must actively revoke ownership from the old operation.
  useEffect(() => {
    const activeOperation = activeOperationRef.current;
    if (activeOperation && activeOperation.chatId !== chatId) {
      abort();
    }
  }, [abort, chatId]);

  const isCurrentOperation = useCallback(
    (operation: StreamOperation<T, U>) =>
      activeOperationRef.current?.opId === operation.opId &&
      activeOperationRef.current?.chatId === operation.chatId &&
      selectedChatIdRef.current === operation.chatId,
    []
  );

  const updateCurrentOperation = useCallback(
    (
      operation: StreamOperation<T, U>,
      updater: (current: StreamOperation<T, U>) => StreamOperation<T, U>
    ): StreamOperation<T, U> | null => {
      const current = activeOperationRef.current;
      if (
        !current ||
        current.opId !== operation.opId ||
        current.chatId !== operation.chatId
      ) {
        return null;
      }
      const updated = updater(current);
      activeOperationRef.current = updated;
      return updated;
    },
    []
  );

  const setBlockedError = useCallback(
    (chatId: T, message: string, source: StreamMode) => {
      setBlockedChatState({ chatId, message, source });
    },
    []
  );

  const beginOperation = useCallback(
    ({
      mode,
      chatId,
      assistantMessageId,
      userMessageId,
    }: {
      mode: StreamMode;
      chatId: T;
      assistantMessageId: U;
      userMessageId: U;
    }): StreamOperation<T, U> => {
      // Starting new work always invalidates any older stream ownership first.
      abortReasonRef.current = null;
      streamAbortRef.current?.abort();
      streamAbortRef.current = null;
      clearBlockedStateForChat(chatId);

      const operation: StreamOperation<T, U> = {
        opId: nextOperationIdRef.current + 1,
        mode,
        chatId,
        assistantMessageId,
        userMessageId,
        lastEventIndex: -1,
        lastActivityAt: Date.now(),
      };
      nextOperationIdRef.current = operation.opId;
      activeOperationRef.current = operation;
      setSendingOperation({
        chatId,
        opId: operation.opId,
      });
      return operation;
    },
    [clearBlockedStateForChat]
  );

  const startStallTimer = useCallback(
    ({
      operation,
      abortController,
    }: {
      operation: StreamOperation<T, U>;
      abortController: AbortController;
    }) => {
      const intervalId = setInterval(() => {
        if (!isCurrentOperation(operation)) {
          return;
        }
        const state = activeOperationRef.current;
        if (!state) {
          return;
        }
        const idleMs = Date.now() - state.lastActivityAt;
        if (idleMs >= streamStallMs) {
          // The stall lifecycle event is emitted from the retry loop when
          // the next attempt actually starts, so analytics stays 1:1 with retries.
          abortReasonRef.current = "stall";
          abortController.abort();
        }
      }, 1000);

      return () => {
        clearInterval(intervalId);
      };
    },
    [isCurrentOperation, streamStallMs]
  );

  const attemptRecoveryAfterFailure = useCallback(
    async ({
      operation,
      chatId,
      userMessageId,
    }: {
      operation: StreamOperation<T, U>;
      chatId: T;
      userMessageId: U;
    }): Promise<RunStreamResult> => {
      try {
        // Recovery is intentionally two-step: first inspect persisted state
        // without mutating UI, then refresh from canonical DB state only if the
        // assistant response was actually saved.
        const reloaded = await fetchChat(chatId);
        if (!isCurrentOperation(operation)) {
          return "stale";
        }
        if (
          !reloaded ||
          !hasSuccessfulAssistantAfterUser({
            chat: reloaded,
            userMessageId,
          })
        ) {
          return "failed";
        }
        await refreshChat(chatId);
        if (!isCurrentOperation(operation)) {
          return "stale";
        }
        return "recovered";
      } catch (error) {
        console.warn(
          "[Stream] Failed to reload chat after stream error",
          error
        );
        if (!isCurrentOperation(operation)) {
          return "stale";
        }
        return "failed";
      }
    },
    [fetchChat, isCurrentOperation, refreshChat]
  );

  const runStreamWithRetry = useCallback(
    async ({
      operation,
      request,
      surfaceErrors,
    }: {
      operation: StreamOperation<T, U>;
      request: {
        chatId: T;
        messageId: U;
        message: string;
        replayOnly?: boolean;
        isNewChat?: boolean;
        sendOptions?: TSendOptions;
        files?: FileRef[];
      };
      surfaceErrors: boolean;
    }): Promise<RunStreamResult> => {
      let hasStarted = false;
      const terminalEventRef = { current: false };

      for (let attempt = 0; attempt <= RETRY_BACKOFF_MS.length; attempt += 1) {
        if (!isCurrentOperation(operation)) {
          return "stale";
        }

        const state = activeOperationRef.current;
        if (!state) {
          return "stale";
        }

        const replayFrom =
          state.lastEventIndex >= 0 ? state.lastEventIndex + 1 : 0;

        terminalEventRef.current = false;
        abortReasonRef.current = null;
        updateCurrentOperation(operation, (current) => ({
          ...current,
          lastActivityAt: Date.now(),
        }));

        const abortController = new AbortController();
        streamAbortRef.current = abortController;
        const stopStallTimer = startStallTimer({
          operation,
          abortController,
        });

        try {
          const shouldReplayOnly = hasStarted && !request.replayOnly;
          const effectiveReplayOnly = shouldReplayOnly
            ? true
            : request.replayOnly;
          const effectiveIsNewChat = shouldReplayOnly
            ? false
            : request.isNewChat;

          const onEvent = (envelope: IndexedAgentEvent) => {
            handleStreamEvent(envelope, {
              operation,
              activeOperationRef,
              terminalEventRef,
              isCurrentOperation,
              upsertAssistantMessageInChat,
              updateChatMetadataForChat,
              setAgentRunIdForChat,
              currentChatRef,
              onStreamEvent,
              onOperationEvent,
            });
          };

          const onStreamActivity = () => {
            updateCurrentOperation(operation, (current) => ({
              ...current,
              lastActivityAt: Date.now(),
            }));
          };

          await onSend({
            chatId: request.chatId,
            messageId: request.messageId,
            message: request.message,
            replayFrom,
            replayOnly: effectiveReplayOnly,
            isNewChat: effectiveIsNewChat,
            files: request.files,
            sendOptions: request.sendOptions,
            signal: abortController.signal,
            onEvent,
            onStreamActivity,
            onStarted: () => {
              hasStarted = true;
              onStreamActivity();
            },
          });

          if (terminalEventRef.current) {
            return "completed";
          }

          throw new RetryableStreamError({
            message: "Stream ended without terminal event",
          });
        } catch (error) {
          // Manual abort clears `activeOperationRef`, so the manual-abort check
          // must run before the stale-operation check or the aborted lifecycle
          // event would be missed.
          const abortedManually = abortReasonRef.current === "manual";
          if (abortedManually) {
            safelyInvoke("onOperationEvent", () =>
              onOperationEvent?.({
                type: "aborted",
                chatId: request.chatId,
                userMessageId: request.messageId,
                assistantMessageId: operation.assistantMessageId,
                mode: operation.mode,
              })
            );
            return "aborted";
          }

          if (!isCurrentOperation(operation)) {
            return "stale";
          }

          const abortedForStall = abortReasonRef.current === "stall";
          const hasTurnEnded =
            error instanceof ChatStreamError &&
            error.status === ANALYSIS_CHAT_TURN_ENDED_STATUS;
          const retryable =
            abortedForStall ||
            error instanceof RetryableStreamError ||
            !(error instanceof ChatStreamError);

          if (retryable && attempt < RETRY_BACKOFF_MS.length) {
            safelyInvoke("onOperationEvent", () =>
              onOperationEvent?.({
                type: "retry",
                chatId: request.chatId,
                userMessageId: request.messageId,
                assistantMessageId: operation.assistantMessageId,
                mode: operation.mode,
                attempt,
                reason: abortedForStall ? "stall" : "error",
              })
            );

            const waitMs = RETRY_BACKOFF_MS[attempt];
            await sleep(waitMs);
            continue;
          }

          if (retryable) {
            safelyInvoke("onOperationEvent", () =>
              onOperationEvent?.({
                type: "retry-exhausted",
                chatId: request.chatId,
                userMessageId: request.messageId,
                assistantMessageId: operation.assistantMessageId,
                mode: operation.mode,
              })
            );

            const recoveryResult = await attemptRecoveryAfterFailure({
              operation,
              chatId: request.chatId,
              userMessageId: request.messageId,
            });
            if (recoveryResult !== "failed") {
              return recoveryResult;
            }
          }

          // The run has closed, so retrying cannot help; the saved chat may still hold its answer.
          if (hasTurnEnded) {
            const recoveryResult = await attemptRecoveryAfterFailure({
              operation,
              chatId: request.chatId,
              userMessageId: request.messageId,
            });
            if (recoveryResult !== "failed") {
              return recoveryResult;
            }
          }

          if (surfaceErrors && isCurrentOperation(operation)) {
            if (!(error instanceof ChatStreamError)) {
              logger.captureException(
                error instanceof Error
                  ? error
                  : new Error(
                      typeof error === "string" ? error : "Unknown stream error"
                    ),
                {
                  chatId: request.chatId,
                  messageId: request.messageId,
                  replayOnly: request.replayOnly ?? false,
                  isNewChat: request.isNewChat ?? false,
                  attempt,
                  abortedForStall,
                },
                reasonTag.withValue("chat_stream_retry_exhausted")
              );
            }

            const errorMessage = getUserFacingStreamError({ error });
            setBlockedError(request.chatId, errorMessage, operation.mode);
            safelyInvoke("onOperationEvent", () =>
              onOperationEvent?.({
                type: "error",
                chatId: request.chatId,
                userMessageId: request.messageId,
                assistantMessageId: operation.assistantMessageId,
                mode: operation.mode,
                error: errorMessage,
                source: "retry",
              })
            );
            upsertLastIteration({
              chatId: request.chatId,
              upsertAssistantMessageInChat,
              assistantMessageId: operation.assistantMessageId,
              updater: (iteration) => ({
                ...iteration,
                error: errorMessage,
                statusMessages: [],
              }),
            });
          }

          return "failed";
        } finally {
          stopStallTimer();
          if (streamAbortRef.current === abortController) {
            streamAbortRef.current = null;
          }
        }
      }

      return "failed";
    },
    [
      attemptRecoveryAfterFailure,
      isCurrentOperation,
      onOperationEvent,
      onSend,
      onStreamEvent,
      setAgentRunIdForChat,
      setBlockedError,
      startStallTimer,
      updateCurrentOperation,
      updateChatMetadataForChat,
      upsertAssistantMessageInChat,
    ]
  );

  const handleSendMessage = useCallback(
    async (message: string, files?: FileRef[], sendOptions?: TSendOptions) => {
      console.log(
        "[useChatStreaming] handleSendMessage called with message:",
        message,
        "files:",
        files?.length ?? 0
      );
      if (sendingOperation !== null || !chat || message.trim().length === 0) {
        return;
      }

      const trimmedMessage = message.trim();
      const targetChatId = chat.id;
      // Use only isPersisted, not chatSelection.isNew, because on sharing pages
      // the chat list may not update (user is null), causing isNew to stay true forever.
      const isFirstMessage = !chat.isPersisted;

      if (isFirstMessage) {
        const newTitle =
          trimmedMessage.length > 100
            ? trimmedMessage.slice(0, 100) + "..."
            : trimmedMessage;
        updateChatMetadataForChat(targetChatId, {
          title: newTitle,
          updatedAt: new Date(),
        });
      }

      const userMessageId = uuidv4() as U;
      const assistantMessageId = uuidv4() as U;
      const timestamp = new Date();

      appendMessageToChat(targetChatId, {
        id: userMessageId,
        role: "user",
        text: trimmedMessage,
        timestamp,
        files,
      });

      const operation = beginOperation({
        mode: "send",
        chatId: targetChatId,
        assistantMessageId,
        userMessageId,
      });

      try {
        await runStreamWithRetry({
          operation,
          request: {
            chatId: targetChatId,
            messageId: userMessageId,
            message: trimmedMessage,
            isNewChat: isFirstMessage,
            sendOptions,
            files,
          },
          surfaceErrors: true,
        });
      } finally {
        finishOperationIfCurrent(operation);
      }
    },
    [
      appendMessageToChat,
      beginOperation,
      chat,
      finishOperationIfCurrent,
      runStreamWithRetry,
      sendingOperation,
      updateChatMetadataForChat,
    ]
  );

  const handleRetryMessage = useCallback(
    async ({
      assistantMessageId,
      userMessage,
      sendOptions,
    }: {
      assistantMessageId: U;
      userMessage: Extract<ChatMessage<U>, { role: "user" }>;
      sendOptions?: TSendOptions;
    }) => {
      if (sendingOperation !== null || !chat) {
        return;
      }

      const targetChatId = chat.id;
      const operation = beginOperation({
        mode: "retry",
        chatId: targetChatId,
        assistantMessageId,
        userMessageId: userMessage.id,
      });

      try {
        const recoveryResult = await attemptRecoveryAfterFailure({
          operation,
          chatId: targetChatId,
          userMessageId: userMessage.id,
        });
        if (recoveryResult !== "failed") {
          return;
        }

        if (!isCurrentOperation(operation)) {
          return;
        }

        upsertAssistantMessageInChat(
          targetChatId,
          assistantMessageId,
          (existing) => {
            const baseMessage = getAssistantMessage({
              existing,
              assistantMessageId,
              fallbackTimestamp: new Date(),
            });
            return {
              ...baseMessage,
              timestamp: new Date(),
              iterations: [createEmptyIteration()],
            };
          }
        );

        await runStreamWithRetry({
          operation,
          request: {
            chatId: targetChatId,
            messageId: userMessage.id,
            message: userMessage.text,
            isNewChat: !chat.isPersisted,
            sendOptions,
            files: userMessage.files,
          },
          surfaceErrors: true,
        });
      } finally {
        finishOperationIfCurrent(operation);
      }
    },
    [
      attemptRecoveryAfterFailure,
      beginOperation,
      chat,
      finishOperationIfCurrent,
      isCurrentOperation,
      runStreamWithRetry,
      sendingOperation,
      upsertAssistantMessageInChat,
    ]
  );

  const tryResumeStream = useCallback(
    async (resumeChat: LoadedChat<T, U>) => {
      if (!resumeChat.isPersisted || resumeChat.messages.length === 0) {
        return;
      }

      // Prefer the server-provided pending signal, since intermediate tool-call
      // iterations are persisted mid-turn and cause the trailing DTO message to
      // render as an assistant even while the workflow is still running.
      // Fallback: resume when the last DTO message is a user message (used by
      // surfaces that don't yet compute pendingMessageId server-side).
      let pendingUser: UserFrontendChatMessage<U> | undefined;
      if (resumeChat.pendingMessageId) {
        const match = resumeChat.messages.find(
          (msg) => msg.role === "user" && msg.id === resumeChat.pendingMessageId
        );
        if (match && match.role === "user") {
          pendingUser = match;
        }
      } else {
        const lastMessage = resumeChat.messages[resumeChat.messages.length - 1];
        if (lastMessage.role === "user") {
          pendingUser = lastMessage;
        }
      }
      if (!pendingUser) {
        return;
      }

      console.log(
        `[Resume] Attempting to resume stream for message ${pendingUser.id}`
      );

      const assistantMessageId = uuidv4() as U;
      const operation = beginOperation({
        mode: "resume",
        chatId: resumeChat.id,
        assistantMessageId,
        userMessageId: pendingUser.id,
      });

      try {
        const result = await runStreamWithRetry({
          operation,
          request: {
            chatId: resumeChat.id,
            messageId: pendingUser.id,
            message: pendingUser.text,
            replayOnly: true,
          },
          surfaceErrors: false,
        });

        if (
          result === "aborted" ||
          result === "stale" ||
          result === "recovered"
        ) {
          return;
        }

        if (!isCurrentOperation(operation)) {
          return;
        }

        const reloaded = await refreshChat(resumeChat.id);
        if (!isCurrentOperation(operation)) {
          return;
        }

        if (
          !reloaded ||
          !hasSuccessfulAssistantAfterUser({
            chat: reloaded,
            userMessageId: pendingUser.id,
          })
        ) {
          const errorMessage = GENERIC_STREAM_ERROR_MESSAGE;
          setBlockedError(resumeChat.id, errorMessage, "resume");
          safelyInvoke("onOperationEvent", () =>
            onOperationEvent?.({
              type: "error",
              chatId: resumeChat.id,
              userMessageId: pendingUser.id,
              assistantMessageId,
              mode: "resume",
              error: errorMessage,
              source: "retry",
            })
          );
          upsertAssistantMessageInChat(
            resumeChat.id,
            assistantMessageId,
            (existing) => {
              const baseMessage = getAssistantMessage({
                existing,
                assistantMessageId,
                fallbackTimestamp: new Date(),
              });
              return {
                ...baseMessage,
                iterations: [
                  {
                    ...createEmptyIteration(),
                    error: errorMessage,
                  },
                ],
              };
            }
          );
        }
      } finally {
        finishOperationIfCurrent(operation);
      }
    },
    [
      beginOperation,
      finishOperationIfCurrent,
      isCurrentOperation,
      onOperationEvent,
      refreshChat,
      runStreamWithRetry,
      setBlockedError,
      upsertAssistantMessageInChat,
    ]
  );

  return {
    isSending: sendingOperation?.chatId === chatId,
    streamError:
      blockedChatState?.chatId === chatId ? blockedChatState.message : null,
    handleSendMessage,
    handleRetryMessage,
    tryResumeStream,
    abort,
  };
};
